<template>
  <main role="main">
    <notification />
    <error-notification />
    <banner @menu="toggleMenu" />
    <side-menu :opened="menuDisplay" @close="toggleMenu">
      <nav slot="content" class="sidebar-nav">
        <div v-if="allowWorksFor" class="works-for">
          <user-information
            :first-name="account.firstName"
            :last-name="account.lastName"
            :email="account.email"
            :vertical-responsive="true"
            :other="isLoggedInAsOther"
          />
          <button type="button" class="outline icon before" @click="switchUser()">
            <svgicon name="switch" />
            <span v-t>Switch Users</span>
          </button>
        </div>

        <ul>
          <li>
            <router-link v-t :to="'/app/clients'" @click.native="toggleMenu">Client List</router-link>
          </li>
          <li>
            <button v-t @click="showModal('profile')">Profile</button>
          </li>
          <li v-if="showPaymentInformationLink">
            <button v-t @click="showModal('profile-payment')">Payment Information</button>
          </li>
          <li v-if="showSignupLink">
            <router-link v-t :to="'/app/signup-link'" @click.native="toggleMenu">Sign Up Link</router-link>
          </li>
        </ul>

        <ul>
          <li v-if="showSupportLinks">
            <button v-t @click="showMenuAndIntercom()">Support</button>
          </li>
          <li v-if="showSupportLinks">
            <a v-t :href="advisorToolsUrl" target="_blank">FAQ</a>
          </li>
          <li v-if="showPaymentInformationLink">
            <a v-t :href="advisorServiceAgreementUrl" target="_blank">Advisor Service Agreement</a>
          </li>
        </ul>

        <router-link id="signout" :to="'/logout'" class="icon before">
          <svgicon class="signout-icon" name="signout" :alt="$t('Sign out')" />
          <span v-t>Sign Out</span>
        </router-link>
      </nav>
    </side-menu>

    <nav v-if="showMobileNav()" class="mobile-nav" role="menubar">
      <router-link v-t :to="'/app/clients'" role="menuitem">Clients</router-link>
      <router-link v-t :to="'/app/overview'" role="menuitem">Overview</router-link>
      <router-link v-t :to="'/app/revenue'" role="menuitem">Revenue</router-link>
      <router-link v-if="showAdvisorResources" v-t :to="'/app/resources'" role="menuitem">Resources</router-link>
    </nav>
    <component :is="modalComponent" :displayed="modalDisplay" @close="closeModal" />
    <router-view />
  </main>
</template>

<script>
import '@/lib/icons/signout';
import '@/lib/icons/switch';
import getBrandConfig from '@/lib/utils/get_brand_config';
import {
  banner,
  sideMenu,
  modal,
  notification,
  errorNotification,
  userInformation,
} from '@/components/layout';
import { profile, profilePayment } from '@/components/advisor';
import { mapGetters, mapActions } from '@/lib/vue';
import { checkAuth } from '@/lib/auth';
import { intercom, init } from '@/lib/analytics';
import { detectLanguage, changeLanguage } from '@wealthbar/i18n';
import errorStore from '@/store/error';

const _ = {
  checkAuth,
  intercom,
  init,
  brandConfig: getBrandConfig(),
  window,
  detectLanguage,
  changeLanguage,
  errorStore,
};

export default {
  _,
  name: 'App',

  route: {
    meta: { requiresAuth: true },
  },
  head() {
    return {
      title: `${_.brandConfig.fullName}`,
    };
  },

  components: {
    banner,
    sideMenu,
    modal,
    notification,
    profile,
    profilePayment,
    userInformation,
    errorNotification,
  },

  async beforeRouteEnter(to, from, next) {
    const authorizedToPath = await _.checkAuth(to.path);

    if (authorizedToPath && _.brandConfig.allowWorksFor) {
      const comesFromAuthorizedReferrer = to.query.skipWorksFor === 'true';
      if (comesFromAuthorizedReferrer) {
        _.window.localStorage.setItem('hasWorksForAccess', true);
      }
      const hasWorksForAccess = _.window.localStorage.getItem(
        'hasWorksForAccess',
      );

      if (hasWorksForAccess) {
        next();
      } else {
        next('/worksfor');
      }
    } else if (authorizedToPath) {
      next();
    }

    _.init();
  },

  data() {
    return {
      languagePreference: null,
      menuDisplay: false,
      errorBannerDisplay: false,
      modalDisplay: false,
      modalComponent: '',
      showAdvisorResources: _.brandConfig.advisorResources,
      showPaymentInformationLink: _.brandConfig.paymentInformationLink,
      showSignupLink: _.brandConfig.signupLink,
      showAdvisorServiceAgreementLink:
        _.brandConfig.advisorServiceAgreementLink,
      advisorServiceAgreementUrl: _.brandConfig.advisorServiceAgreementUrl,
      advisorToolsUrl: _.brandConfig.advisorToolsUrl,
      allowWorksFor: _.brandConfig.allowWorksFor,
      showSupportLinks: _.brandConfig.supportLinks,
      userFullName: null,
      userInitials: null,
      userEmail: null,
      isLoggedInAsOther: false,
    };
  },

  computed: {
    ...mapGetters(['account']),
  },

  async created() {
    try {
      await this.fetchAccount();
      if ((typeof this.account.languagePreference === 'undefined') || !this.account.languagePreference) {
        await _.detectLanguage();
        this.account.languagePreference = this.languagePreference;
        const account = Object.assign(this.account, {
          languagePreference: this.account.languagePreference,
        });
        await this.updateAccount(account);
      }

      const language = (this.account.languagePreference && this.account.languagePreference.slice(0, 2) === 'fr') ? 'fr-CA' : 'en-CA';
      await _.changeLanguage(language);

      this.userFullName = `${this.account.firstName} ${this.account.lastName}`;
      this.userInitials = this.account.firstName.charAt(0).toUpperCase()
        + this.account.lastName.charAt(0).toUpperCase();
      this.userEmail = this.account.email;
      if (_.window.localStorage.getItem('originalAdvisorEmail')) {
        this.isLoggedInAsOther = !(
          _.window.localStorage.getItem('originalAdvisorEmail') === this.userEmail
        );
      }
    } catch (exception) {
      _.errorStore.addError();
    }
  },

  methods: {
    ...mapActions(['fetchAccount', 'updateAccount']),
    toggleMenu() {
      this.menuDisplay = !this.menuDisplay;
    },

    showModal(comp) {
      this.menuDisplay = false;
      this.modalComponent = comp;
      this.modalDisplay = true;
    },

    closeModal() {
      this.modalDisplay = false;
    },

    switchUser() {
      _.window.location.assign('/worksfor');
    },

    showMenuAndIntercom() {
      _.intercom('show');
      this.toggleMenu();
    },

    showIntercom() {
      _.intercom('show');
    },

    closeErrorBanner() {
      _.errorStore.reset();
    },

    showMobileNav() {
      const routesToHide = [
        '/app/signup-link',
        '/app/clients/:id/overview',
        '/app/clients/:id/:accountId',
      ];
      for (const route of this.$route.matched) {
        if (routesToHide.includes(route.path)) {
          return false;
        }
      }
      return true;
    },
  },
};
</script>
<style lang="scss" scoped>

.sidebar-nav {
  > * { border-bottom: 1px solid $neutral-100; }
  ul {
    margin: 0;
    padding: 0.5rem 0;
  }
  li {
    border: 0;
    padding: 0;
  }
  a, button {
    text-align: left;
    display: block;
    width: 100%;
    padding: 0.5rem 2rem;
    text-decoration: none;
    color: $black;
    &:hover,
    &:focus {
      color: $primary-500;
      background: $bg-50;
    }
  }
}

#signout {
  margin: 0.5rem 0;
  padding: 0.5rem 2rem;
  justify-content: flex-start;
  border: none;
}

.works-for {
  padding: 1rem 2rem;
  button {
    width: 100%;
    margin: 1.5rem 0 0;
    @media #{$screen-width-medium} { width: auto; }
  }
}
</style>
